// ===== components/AdminTable.jsx =====
import { useEffect, useMemo, useState } from "react";
import DataTable, { createTheme } from "react-data-table-component";
import { utils, write } from "xlsx";
import { saveAs } from "file-saver";

createTheme(
  "bmcneutral",
  {
    text: { primary: "#111827", secondary: "#6B7280" },
    background: { default: "#FFFFFF" },
    context: { background: "#E5E7EB", text: "#111827" },
    divider: { default: "#E5E7EB" },
    button: { default: "#E5E7EB", hover: "#D1D5DB", focus: "#0EA5E9", disabled: "#9CA3AF" },
    highlightOnHover: { default: "#F3F4FF", text: "#111827" },
    striped: { default: "#F9FAFB", text: "#111827" },
  },
  "light"
);

export default function AdminTable({
  columns,
  rows,
  title,
  subtitle = "תצוגה, סינון וייצוא נתונים",
  searchKeys = [],
  exportKeys = [],
  extraActions = null,
  loading = false,
  filters = null, // ✅ יוצג ליד החיפוש
  pageSizeOptions = [10, 5, 3, 2, 1], // ✅ כמו בעמוד שהראית
}) {
  const [query, setQuery] = useState("");

  // ✅ פג'יניישן בסגנון RecivedOffersAdmin
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(pageSizeOptions?.[0] ?? 10);

  const safeRows = Array.isArray(rows) ? rows : [];

  const filteredRows = useMemo(() => {
    if (!query.trim()) return safeRows;
    const q = query.toLowerCase();

    if (!searchKeys.length) {
      return safeRows.filter((r) =>
        Object.values(r ?? {})
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q))
      );
    }

    return safeRows.filter((r) =>
      searchKeys
        .map((k) => r?.[k])
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [safeRows, query, searchKeys]);

  // ✅ איפוס לעמוד 1 כשמשתנה חיפוש
  useEffect(() => {
    setPage(1);
  }, [query]);

  const totalItems = filteredRows.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

  // ✅ אם ירדנו במספר עמודים (אחרי פילטר) – לא להישאר בעמוד שלא קיים
  useEffect(() => {
    setPage((p) => Math.min(p, totalPages));
  }, [totalPages]);

  const pagedRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredRows.slice(start, start + pageSize);
  }, [filteredRows, page, pageSize]);

  const buildExportRows = () => {
    if (!exportKeys.length) return filteredRows;
    return filteredRows.map((r) => {
      const obj = {};
      for (const k of exportKeys) obj[k] = r?.[k] ?? "";
      return obj;
    });
  };

  const exportExcel = () => {
    const exportData = buildExportRows();
    if (!exportData.length) {
      alert("אין נתונים לייצוא");
      return;
    }

    const wb = utils.book_new();
    const ws = utils.json_to_sheet(exportData);
    utils.book_append_sheet(wb, ws, title || "Sheet1");
    const arrayBuffer = write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([arrayBuffer], { type: "application/octet-stream" }), `${title || "export"}.xlsx`);
  };

  const customStyles = {
    table: {
      style: {
        overflow: "hidden",
        direction: "rtl",
        minHeight: "73vh",
        backgroundColor: "#FFFFFF",
        border: "none",
      },
    },
    headRow: {
      style: {
        backgroundColor: "#F3F4F6",
        borderBottom: "1px solid #E5E7EB",
        minHeight: "42px",
      },
    },
    headCells: {
      style: {
        color: "#6B7280",
        fontSize: "11px",
        fontWeight: 600,
        letterSpacing: "0.03em",
      },
    },
    rows: {
      style: { fontSize: "13px", minHeight: "48px", color: "#111827" },
      highlightOnHoverStyle: { backgroundColor: "#F3F4FF", transition: "background-color 120ms ease-out" },
      stripedStyle: { backgroundColor: "#F9FAFB" },
    },
    cells: { style: { paddingTop: 10, paddingBottom: 10 } },
  };

  return (
    <div className="admin-table-wrapper">
      <div className="table-card bg-white border border-slate-200 overflow-hidden">
        {/* HEADER */}
        <div className="table-header flex flex-wrap items-center justify-between gap-3 px-4 pt-6 pb-4 border-b border-slate-200 bg-slate-50">
          <div className="title-wrap flex flex-col items-start min-w-[160px]">
            <h2 className="text-slate-800 text-lg font-semibold tracking-tight">{title}</h2>
            {subtitle && <p className="subtitle text-slate-500 text-xs mt-1">{subtitle}</p>}
          </div>

          <div className="flex flex-wrap items-center justify-end gap-2 min-w-0">
            {extraActions && <div className="flex flex-wrap items-center gap-2">{extraActions}</div>}
            {filters && <div className="flex items-center">{filters}</div>}

            <button
              type="button"
              style={{ color: "white", paddingTop: "6.5px", paddingBottom: "8.5px" }}
              className="inline-flex items-center gap-2 rounded-full bg-slate-700 px-4 py-1.5 text-xs font-semibold text-white hover:bg-slate-600 transition"
              onClick={exportExcel}
            >
              ↓ Excel
            </button>

            <div className="relative w-[190px] max-w-xs">
              <input
                type="text"
                placeholder="חיפוש..."
                aria-label="Search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="bg-white border border-slate-300 rounded-full pr-8 pl-3 py-1.5 text-xs text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-sky-500 focus:border-sky-500 w-full"
              />
              <span className="pointer-events-none absolute right-2.5 top-1.5 text-[11px] text-slate-400">🔍</span>
            </div>
          </div>
        </div>

        {/* TABLE */}
        <DataTable
          className="admin-data-table"
          columns={columns}
          data={pagedRows}
          theme="bmcneutral"
          customStyles={customStyles}
          highlightOnHover
          striped
          dense
          persistTableHead
          progressPending={loading}
          progressComponent={<div className="py-6 text-center text-slate-500 text-sm">טוען נתונים…</div>}
          noDataComponent={<div className="py-6 text-center text-slate-500 text-sm">אין נתונים להצגה</div>}
        />

        {/* ✅ PAGINATION – כמו בעמוד RecivedOffersAdmin */}
        <div dir="rtl" className="flex items-center justify-between px-4 py-3 bg-slate-50 border-t border-slate-200">
          <div className="text-sm text-gray-600">
            עמוד {page} מתוך {totalPages} | סה״כ רשומות: {totalItems}
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              className="px-3 py-1 border rounded-md disabled:opacity-40"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              הקודם
            </button>

            <button
              type="button"
              className="px-3 py-1 border rounded-md disabled:opacity-40"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              הבא
            </button>

            <div className="relative">
              <select
                dir="rtl"
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
                className="border rounded-md pl-9 pr-3 py-1 text-sm text-right bg-white appearance-none bg-none"
                style={{
                  WebkitAppearance: "none",
                  MozAppearance: "none",
                  appearance: "none",
                  backgroundImage: "none",
                }}
              >
                {pageSizeOptions.map((n) => (
                  <option key={n} value={n}>
                    {n} בעמוד
                  </option>
                ))}
              </select>

              {/* חץ כמו אצלך */}
              <img
                src="/images/img_vector_red_a700_03_10.svg"
                alt="arrow"
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-[10px] w-[10px] opacity-70"
              />
            </div>
          </div>
        </div>
      </div>

      <style jsx global>{`
        /* מבטל כל radius בתוך הטבלה */
        .admin-data-table,
        .admin-data-table * {
          border-radius: 0 !important;
        }

        .admin-data-table .rdt_Table,
        .admin-data-table .rdt_TableWrapper,
        .admin-data-table .rdt_TableHead,
        .admin-data-table .rdt_TableBody {
          border-radius: 0 !important;
        }
      `}</style>

      <style>{`
        /* לא נוגעים בפילטרים למעלה — רק select של הפג'יניישן שלנו (כבר appearance:none) */
        .admin-table-wrapper select::-ms-expand {
          display: none !important;
        }
      `}</style>
    </div>
  );
}
